/**
 * Common labels
 * Dynamic mapping from JSON based on current language
 * Uses getLanguage() to determine which locale to use
 */

import svCommon from '../locales/sv/common.json';
import enCommon from '../locales/en/common.json';
import { getLanguage, type Language } from '@/utils/languageUtils';

/**
 * Helper function to get the correct locale based on language
 */
const getCommonLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enCommon : svCommon;
};

/**
 * Common Button labels - dynamically uses current language
 */
export const commonButtonLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.buttons;
};

/**
 * Common Misc labels - dynamically uses current language
 */
export const commonMiscLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.misc;
};

/**
 * Common Title labels - dynamically uses current language
 */
export const commonTitleLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.titles;
};

/**
 * Common Pagination labels - dynamically uses current language
 */
export const commonPaginationLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.pagination;
};

/**
 * Common Fields labels - dynamically uses current language
 * These are fields that can be used for both person and company (address, city, postal number, etc.)
 */
export const commonFieldsLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.fields || {};
};

/**
 * Common Countries labels - dynamically uses current language
 */
export const commonCountriesLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.countries || {};
};

/** Map from common country value (any language/code) to common.countries key */
const COUNTRY_VALUE_TO_KEY: Record<string, string> = {
  se: "sweden", sverige: "sweden", sweden: "sweden",
  no: "norway", norge: "norway", norway: "norway",
  dk: "denmark", danmark: "denmark", denmark: "denmark",
  fi: "finland", finland: "finland",
  de: "germany", tyskland: "germany", germany: "germany",
  gb: "unitedKingdom", storbritannien: "unitedKingdom", "united kingdom": "unitedKingdom", unitedKingdom: "unitedKingdom",
  nl: "netherlands", nederländerna: "netherlands", netherlands: "netherlands",
  be: "belgium", belgien: "belgium", belgium: "belgium",
  fr: "france", frankrike: "france", france: "france",
  es: "spain", spanien: "spain", spain: "spain",
};

/**
 * Returns the country name in the given language. Accepts country code (se, NO),
 * Swedish name (Sverige), or English name (Sweden). Falls back to original value if unknown.
 */
export const getCountryDisplayName = (value: string | null | undefined, lang?: Language): string => {
  if (value == null || value.trim() === "") return "";
  const normalized = value.trim().toLowerCase();
  const key = COUNTRY_VALUE_TO_KEY[normalized];
  const labels = commonCountriesLabels(lang);
  const translated = key ? labels[key] : undefined;
  return translated ?? value;
};

/**
 * Dashboard label item interface
 */
export interface DashboardLabelItem {
  title: string;
  subtitle: string;
  adminSubtitle?: string;
  disabledTooltip?: string;
  buttonLabel?: string;
  tooltipTitle?: string;
  tooltipDesc?: string;
  newTag?: string;
}

/**
 * Dashboard labels interface
 */
export interface DashboardLabels {
  postAd: DashboardLabelItem;
  settings: DashboardLabelItem;
  listings: DashboardLabelItem;
  messages: DashboardLabelItem;
  orders: DashboardLabelItem;
  myListingsOrders: DashboardLabelItem;
  favorites: DashboardLabelItem;
  economy: DashboardLabelItem;
  support: DashboardLabelItem;
  test: DashboardLabelItem;
  myAuctions: DashboardLabelItem;
  leadbox: DashboardLabelItem;
  dealerPortal: DashboardLabelItem;
  loadingDashboard?: string;
  /** Påminnelse: säljare med annons från test men utan leveransadress */
  deliveryAddressReminder?: {
    title: string;
    description: string;
    cta: string;
  };
  pushTip?: {
    title: string;
    description: string;
    cta: string;
    later: string;
    activating: string;
    dismissAria: string;
  };
  feedbackPrompt?: {
    title: string;
    description: string;
    cta: string;
    later: string;
  };
  celebrationMock?: {
    button: string;
    title: string;
    listingTitle?: string;
    subtitle: string;
  };
  realtimeAlerts?: {
    outbidTitle: string;
    outbidBody: string;
    outbidAction?: string;
  };
  unreadNotifications?: {
    title: string;
    viewAll: string;
    deleteAria: string;
    fallbackTitle: string;
  };
  quickGuide?: Record<string, string>;
  feedback?: Record<string, string>;
}

/**
 * Dashboard labels - dynamically uses current language
 */
export const dashboardLabels = (lang?: Language): DashboardLabels => {
  const locale = getCommonLocale(lang);
  return (locale.dashboard || {}) as DashboardLabels;
};

/**
 * Common Menu labels - dynamically uses current language
 */
export const commonMenuLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.menu || {};
};

export const nativeAppLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return (locale as { nativeApp?: Record<string, string> }).nativeApp || {};
};

/**
 * Common Auth labels - dynamically uses current language
 */
export const commonAuthLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.auth || {};
};

/**
 * Language names (Svenska/Engelska when sv, Swedish/English when en)
 */
export const languageNamesLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return locale.languageNames || { sv: 'Svenska', en: 'Engelska' };
};

/**
 * Validation messages for formValidation utils (name, email, phone, password, address, etc.)
 * Pass to validateName(value, commonValidationLabels(lang).name), etc.
 */
export const commonValidationLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return (locale.validation || {}) as Record<string, string>;
};

/**
 * Date and relative time labels (N/A, Just now, X ago, etc.)
 */
export const commonDateLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return (locale.date || {}) as Record<string, string>;
};

/**
 * 404 NotFound page labels
 */
export const notFoundLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return (locale.notFound || {}) as Record<string, string>;
};

/**
 * Terms acceptance dialog labels (KYC flow)
 */
export const commonTermsAcceptanceLabels = (lang?: Language): Record<string, string> => {
  const locale = getCommonLocale(lang);
  return (locale.termsAcceptance || {}) as Record<string, string>;
};

/**
 * Common labels - returns the entire common.json locale object
 * Used for accessing menuPages and other top-level keys
 */
export const commonLabels = (lang?: Language) => {
  return getCommonLocale(lang);
};



